import { create } from 'zustand';
import axios from 'axios';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';
import { serviceProviderApi } from '@/services/api/serviceProviderApi';
import { socketClient } from '@/services/websocket/socketClient';
import type { ApiResponse, ServiceEngagement, Specialization } from '@/types';

const ENGAGEMENT_EVENTS = {
  CREATED: 'engagement:created',
  ACCEPTED: 'engagement:accepted',
  EXPIRED: 'engagement:expired',
  COMPLETED: 'engagement:completed',
} as const;

interface EngagementEventPayload {
  engagementId: string;
  clientProjectId?: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError<ApiResponse<unknown>>(error)) {
    return error.response?.data?.message ?? error.message ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const requireResponseData = <T,>(response: ApiResponse<T>, fallback: string): T => {
  if (response.data === undefined) {
    throw new Error(fallback);
  }

  return response.data;
};

const isEngagementEventPayload = (payload: unknown): payload is EngagementEventPayload => {
  return typeof payload === 'object' && payload !== null && 'engagementId' in payload;
};

interface EngagementState {
  engagements: ServiceEngagement[];
  activeEngagement: ServiceEngagement | null;
  specializationScope: Specialization | null;
  isLoading: boolean;
  error: string | null;

  fetchEngagements: (clientProjectId: string) => Promise<void>;
  createEngagement: (clientProjectId: string, data: {
    specializationScope: string;
    accessWindow: { startDate: string; endDate: string };
    serviceProviderEmail?: string;
    serviceProviderId?: string;
  }) => Promise<ServiceEngagement>;
  acceptEngagement: (id: string) => Promise<ServiceEngagement>;
  completeEngagement: (id: string) => Promise<ServiceEngagement>;
  revokeEngagement: (id: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;

  // Internal handlers for WebSocket events
  _handleEngagementCreated: (payload: EngagementEventPayload) => void;
  _handleEngagementAccepted: (payload: EngagementEventPayload) => void;
  _handleEngagementExpired: (payload: EngagementEventPayload) => void;
  _handleEngagementCompleted: (payload: EngagementEventPayload) => void;
}

export const useEngagementStore = create<EngagementState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      engagements: [],
      activeEngagement: null,
      specializationScope: null,
      isLoading: false,
      error: null,

      fetchEngagements: async (clientProjectId) => {
        if (get().isLoading) return;

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.listEngagements(clientProjectId);
          const list = requireResponseData(response, 'Failed to fetch engagements');
          set((state) => {
            state.engagements = list;
            state.activeEngagement = list.find(
              (e: ServiceEngagement) => e.status === 'active'
            ) || null;
            state.specializationScope = state.activeEngagement?.specializationScope ?? null;
            state.isLoading = false;
          });
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to fetch engagements');
          });
        }
      },

      createEngagement: async (clientProjectId, data) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.createEngagement(clientProjectId, data);
          const engagement = requireResponseData(response, 'Failed to create engagement');
          set((state) => {
            state.engagements.unshift(engagement);
            if (engagement.status === 'active') {
              state.activeEngagement = engagement;
              state.specializationScope = engagement.specializationScope;
            }
            state.isLoading = false;
          });
          return engagement;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to create engagement');
          });
          throw error;
        }
      },

      acceptEngagement: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.acceptEngagement(id);
          const engagement = requireResponseData(response, 'Failed to accept engagement');
          set((state) => {
            const idx = state.engagements.findIndex((e) => e._id === id);
            if (idx !== -1) state.engagements[idx] = engagement;
            if (engagement.status === 'active') {
              state.activeEngagement = engagement;
              state.specializationScope = engagement.specializationScope;
            }
            state.isLoading = false;
          });
          return engagement;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to accept engagement');
          });
          throw error;
        }
      },

      completeEngagement: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.completeEngagement(id);
          const engagement = requireResponseData(response, 'Failed to complete engagement');
          set((state) => {
            const idx = state.engagements.findIndex((e) => e._id === id);
            if (idx !== -1) state.engagements[idx] = engagement;
            if (state.activeEngagement?._id === id) {
              state.activeEngagement = null;
              state.specializationScope = null;
            }
            state.isLoading = false;
          });
          return engagement;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to complete engagement');
          });
          throw error;
        }
      },

      revokeEngagement: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await serviceProviderApi.revokeEngagement(id);
          set((state) => {
            const idx = state.engagements.findIndex((e) => e._id === id);
            if (idx !== -1) {
              state.engagements[idx].status = 'revoked';
            }
            if (state.activeEngagement?._id === id) {
              state.activeEngagement = null;
              state.specializationScope = null;
            }
            state.isLoading = false;
          });
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to revoke engagement');
          });
          throw error;
        }
      },

      clearError: () => {
        set((state) => { state.error = null; });
      },

      reset: () => {
        set((state) => {
          state.engagements = [];
          state.activeEngagement = null;
          state.specializationScope = null;
          state.isLoading = false;
          state.error = null;
        });
      },

      // WebSocket event handlers
      _handleEngagementCreated: (payload) => {
        // Refetch if we're viewing the same client project
        const { engagements, fetchEngagements } = get();
        if (engagements.length > 0) {
          const currentClientProjectId = engagements[0]?.clientProjectId;
          const { clientProjectId } = payload;
          if (currentClientProjectId && clientProjectId && currentClientProjectId === clientProjectId) {
            fetchEngagements(clientProjectId);
          }
        }
      },

      _handleEngagementAccepted: (payload) => {
        set((state) => {
          const idx = state.engagements.findIndex((e) => e._id === payload.engagementId);
          if (idx !== -1) {
            state.engagements[idx].status = 'active';
          }
        });
      },

      _handleEngagementExpired: (payload) => {
        set((state) => {
          const idx = state.engagements.findIndex((e) => e._id === payload.engagementId);
          if (idx !== -1) {
            state.engagements[idx].status = 'expired';
          }
          if (state.activeEngagement?._id === payload.engagementId) {
            state.activeEngagement = null;
            state.specializationScope = null;
          }
        });
      },

      _handleEngagementCompleted: (payload) => {
        set((state) => {
          const idx = state.engagements.findIndex((e) => e._id === payload.engagementId);
          if (idx !== -1) {
            state.engagements[idx].status = 'completed';
          }
          if (state.activeEngagement?._id === payload.engagementId) {
            state.activeEngagement = null;
            state.specializationScope = null;
          }
        });
      },
    }))
  )
);

// WebSocket listener management
let wsListenersRegistered = false;

export const setupEngagementWebSocketListeners = (): void => {
  if (wsListenersRegistered) return;
  if (!socketClient.isConnected()) return;

  socketClient.onCustomEvent(ENGAGEMENT_EVENTS.CREATED, (...args: unknown[]) => {
    const payload = args[0];
    if (isEngagementEventPayload(payload)) {
      useEngagementStore.getState()._handleEngagementCreated(payload);
    }
  });
  socketClient.onCustomEvent(ENGAGEMENT_EVENTS.ACCEPTED, (...args: unknown[]) => {
    const payload = args[0];
    if (isEngagementEventPayload(payload)) {
      useEngagementStore.getState()._handleEngagementAccepted(payload);
    }
  });
  socketClient.onCustomEvent(ENGAGEMENT_EVENTS.EXPIRED, (...args: unknown[]) => {
    const payload = args[0];
    if (isEngagementEventPayload(payload)) {
      useEngagementStore.getState()._handleEngagementExpired(payload);
    }
  });
  socketClient.onCustomEvent(ENGAGEMENT_EVENTS.COMPLETED, (...args: unknown[]) => {
    const payload = args[0];
    if (isEngagementEventPayload(payload)) {
      useEngagementStore.getState()._handleEngagementCompleted(payload);
    }
  });

  wsListenersRegistered = true;
};

export const cleanupEngagementWebSocketListeners = (): void => {
  if (!wsListenersRegistered) return;

  socketClient.offCustomEvent(ENGAGEMENT_EVENTS.CREATED);
  socketClient.offCustomEvent(ENGAGEMENT_EVENTS.ACCEPTED);
  socketClient.offCustomEvent(ENGAGEMENT_EVENTS.EXPIRED);
  socketClient.offCustomEvent(ENGAGEMENT_EVENTS.COMPLETED);

  wsListenersRegistered = false;
};

// Auto-setup on module load
if (typeof window !== 'undefined') {
  setTimeout(() => {
    if (socketClient.isConnected()) {
      setupEngagementWebSocketListeners();
    }
  }, 1000);

  socketClient.on('connected', () => {
    setupEngagementWebSocketListeners();
  });

  // Reset on logout
  import('../auth/auth-store').then(({ useAuthStore }) => {
    if (typeof useAuthStore.subscribe !== 'function') {
      return;
    }

    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useEngagementStore.getState().reset();
          cleanupEngagementWebSocketListeners();
        }
      }
    );
  }).catch(() => {
  });
}

// Selector hooks
export const useEngagements = () =>
  useEngagementStore(useShallow((s) => ({
    engagements: s.engagements,
    isLoading: s.isLoading,
    error: s.error,
  })));

export const useActiveEngagement = () =>
  useEngagementStore(useShallow((s) => ({
    engagement: s.activeEngagement,
    specializationScope: s.specializationScope,
  })));

export const useSpecializationScope = () =>
  useEngagementStore((s) => s.specializationScope);

export const useEngagementActions = () =>
  useEngagementStore(useShallow((s) => ({
    fetchEngagements: s.fetchEngagements,
    createEngagement: s.createEngagement,
    acceptEngagement: s.acceptEngagement,
    completeEngagement: s.completeEngagement,
    revokeEngagement: s.revokeEngagement,
    clearError: s.clearError,
    reset: s.reset,
  })));
